import { useMemo, useRef } from "react";

import styles from "./index.module.scss";

const PASSPORT_MAX_DIGITS = 9;

function digitsOnly(value: string) {
  return (value ?? "").replace(/\D/g, "");
}

function normalizePassportDigits(input: string) {
  return digitsOnly(input).slice(0, PASSPORT_MAX_DIGITS);
}

function formatPassport(digits: string) {
  const d = normalizePassportDigits(digits);
  const series = d.slice(0, 4);
  const number = d.slice(4, 9);

  let out = series;
  if (d.length > 4) out += ` ${number}`;

  return out;
}

type PassportInputCompactProps = {
  id?: string;
  dataTest?: string;
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  onChange: (next: string) => void;
  onBlur: (next: string) => void;
};

export function PassportInputCompact({
  id,
  dataTest,
  placeholder,
  value,
  disabled,
  onChange,
  onBlur,
}: PassportInputCompactProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const normalizedDigits = useMemo(
    () => normalizePassportDigits(value ?? ""),
    [value],
  );
  const displayValue = useMemo(
    () => formatPassport(normalizedDigits),
    [normalizedDigits],
  );

  const showPlaceholder = normalizedDigits.length === 0;

  const commit = (raw: string) => {
    const nextDigits = normalizePassportDigits(raw);
    onChange(formatPassport(nextDigits));
  };

  return (
    <>
      <input
        id={id}
        data-test={dataTest}
        placeholder={placeholder}
        ref={inputRef}
        disabled={disabled}
        value={displayValue}
        onChange={(event) => commit(event.target.value)}
        onBlur={(event) => {
          commit(event.target.value);
          onBlur(event.target.value);
        }}
      />

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <span
        className={styles.PassportPlaceholder}
        style={{ display: !disabled && showPlaceholder ? undefined : "none" }}
        onMouseDown={(e) => {
          e.preventDefault();
          if (disabled) return;
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
      >
        0000 00000
      </span>
    </>
  );
}
